import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'

import { log } from '@/log'
import { Payment } from '@/models/api/payment/payment'
import { ApiPaymentSetting } from '@/models/api/payment/setting'

import { ApiClient } from './client'
import { ApiService } from './service'

const namespace: string = 'payment'

export interface ListAllowedPaymentsFor {
  /**
   * @deprecated 外部ベンダーツールの都合による偶発的制約に対処するためのものであり、将来的にはこのキーは削除されることが望ましい.
   * @see https://www.chatwork.com/#!rid87550748-1487317407046324224
   * @see https://github.com/my-color/front/issues/5624
   */
  context?: 'subscription'
  /**
   * 決済確認に使用するURL. 決済確認画面や契約確認画面など.
   *
   * Amazon Payなど、外部URLを経由してから決済手続きを進行する決済方法を利用する場合に参照される.
   * <b>パスではなくURL全体(プロトコル + FQDN + パス)を指定すること</b>
   *
   * @example OK
   * "http://localhost:8080/s/order?scope=brand.test"
   * "https://example.com/s/order/subscription/some_sku_id?scope=brand.test"
   * "https://example.com/s/mypage/reorder/some_order_id/payment"
   *
   * @example NG
   * "/s/order?scope=brand.test"
   * "../order?scope=brand.test"
   * "./payment?scope=brand.test"
   */
  confirm_url: string
}

export default class PaymentService extends ApiService {
  /**
   * 指定の商品を購入する時に利用可能な支払い方法の一覧を取得する
   */
  public async listAllowedPaymentsFor(
    cartIdList: string[],
    query: ListAllowedPaymentsFor
  ): Promise<Either<Error, Payment[]>> {
    try {
      const response = await ApiClient
        .get(`/api/user/payment`)
        .query({
          cartIds: cartIdList.join(','),
          ...query,
        })

      log.debug({ service: `${namespace}/get`, response })
      const paymentList = _.map(response.body.data, data => new Payment(data))

      return new Right(paymentList)
    } catch (error) {
      return new Left(error)
    }
  }

  /**
   * 再注文で利用可能な支払い方法の一覧を取得する.
   *
   * <b>再注文の場合、一部の支払い方法は制限されている点に注意</b>.
   *
   * @see https://github.com/my-color/front/pull/7085 Amazon Pay導入時のPR
   * @see https://github.com/my-color/front/issues/5115#issuecomment-803841009 Amazon Pay以外の支払い方法に再注文での利用が制限されている理由
   * @see \App\Controller\Api\User\Payment\AvailablePaymentForReorderController::setupAvailablePaymentContext 実際の制限
   */
  public async getByOrderId(orderId: string): Promise<Either<Error, Payment[]>> {
    /*
     * 再注文の場合、その直接の原因となった「支払い方法不備」「ユーザー情報不備」の変更さえ行えば、
     * そのまま注文確認画面を経由することなく再注文が行われる仕組みになっている.
     * （注意：再注文が可能となるトリガーは、2022/12/26時点ではNP後払いのみ）
     * つまり、通常の注文は注文確認画面を経由してそこから各種情報を変更してから確定という手順だが、再注文はそのフローを逸脱している.
     * これはユーザーが操作する手数を減らすことにはなっているが、一方で
     * 「通常のフローと大きく異なるため、注文までに他サイトを経由する場合は、同様に特殊な専用フローが必要になる」
     * という問題を引き起こしている.
     *
     * このことを前提として、Amazon Payやキャリア決済といった外部サイトを経由する決済方法を再注文でも利用可能にする場合、
     * 大まかに2つの方針を検討した.
     * A. 再注文のフローも通常のフローと同じく、"各種情報を設定<->(再)注文確認画面->注文確定"という手順にする
     *   - この場合、細かなパラメータなどはDIなどを利用することによって全体の処理を統合できる想定
     *   - 支払い方法や配送先など、任意の情報更新を伴っての再注文を考慮する必要がある
     *     - 文脈によって操作を制限する、ということはできるかもしれない
     *   - UI上の操作数は増える
     * B. 外部サイトを経由する支払い方法ごとに、再注文専用の特殊フローを実装する
     *   - 通常注文と再注文は全く異なるフローとなるため、外部サイト経由系の決済方法サポートが増えるたびに再注文専用フローを新規実装する必要が出てくる
     *     - 実装工数も増大する他、実装自体もほぼ必然的に複雑化・肥大化する
     *   - UI上の操作数は減る
     *
     * 内部的な仕組みとしては、特殊実装が都度必要になると個別の考慮や実装が必要となるためA.の方向に倒したい.
     * （実際、キャリア決済など外部サイトを経由するサービスは実装上の都合から再注文ではブロックされている）
     * 一方でユーザー向けの観点から考える場合、見かけ上のUI操作が増えるにしても「決済方法のついでに、引っ越したので配送先も変更する」といった操作も可能となる.
     * 自由度も増えるので、むしろ操作範囲を固定しないA.の方がユーザーは自身のニーズを満たしやすい可能性すらもある.
     * 内部都合からも、ユーザー向けの観点からも、A.の方針で再構築した方が好ましい効果は大きいと思われる.
     *
     * しかし、そもそもの発端となったAmazon Pay対応時点では A. B. いずれの方針を取るのもスケジュールの想定上、非常に困難と判断した.
     * そのため、やむを得ず<b>再注文に関してはAmazon Payも他の外部サイト経由系と同様に利用不可</b>という前提を取っている.
     * この選定を取った結果として、このメソッドでは PaymentService.get とは異なり、
     * confirm_url をクエリパラメータとしては渡さない実装を取っている.
     */
    return this.getPaymentsBy(`/api/user/payment/order/${orderId}`, this.getByOrderId.name)
  }
  public async getByContractedSubscriptionId(id: string): Promise<Either<Error, Payment[]>> {
    /*
     * Amazon Payは都度払いと定期払いでI/Fが異なる.
     * https://github.com/my-color/front/issues/4300 の対応時点では都度払いしか対応しない.
     * 定期払いで必要になるパラメータは、別途実装開始のタイミングで検討する.
     */
    return this.getPaymentsBy(
      `/api/user/payment/subscription/contracted/${id}`,
      this.getByContractedSubscriptionId.name
    )
  }

  public async setting(): Promise<Either<Error, ApiPaymentSetting[]>> {
    try {
      const response = await ApiClient.get(`/api/common/payment/setting`)

      log.debug({ service: `${namespace}/setting`, response })
      const settingList = _.map(response.body.data, data => new ApiPaymentSetting(data))

      return new Right(settingList)
    } catch (error) {
      return new Left(error)
    }
  }

  private async getPaymentsBy(path: string, service: string): Promise<Either<Error, Payment[]>> {
    try {
      const response = await ApiClient.get(path)

      log.debug({ service: `${namespace}/${service}`, response })
      const paymentList = _.map(response.body.data, data => new Payment(data))

      return new Right(paymentList)
    } catch (error) {
      return new Left(error)
    }
  }
}
